export type MarketplaceIconData = {
  type?: string;
  logo_uri?: string;
  logo?: string;
  marketplace_id?: string;
  _id?: string;
  [key: string]: unknown;
};

export type MetadataMarketplace = {
  icon?: string;
  text?: string;
  key?: string;
};

export type StaticMetadata = {
  marketplaces?: Record<string, MetadataMarketplace>;
  [key: string]: unknown;
};

export type OrgMarketplace = {
  _id?: string;
  name?: string;
  type?: string;
  logo_uri?: string;
  marketplace_id?: string[];
  [key: string]: unknown;
};

/**
 * Finds a marketplace in orgMarketplaces array by various matching strategies.
 */
function findOrgMarketplace(
  key: string,
  marketData: MarketplaceIconData | null | undefined,
  orgMarketplaces: OrgMarketplace[] | unknown[],
): OrgMarketplace | undefined {
  if (!orgMarketplaces || !Array.isArray(orgMarketplaces)) {
    return undefined;
  }

  const markets = orgMarketplaces as OrgMarketplace[];
  const keyLower = key?.toLowerCase?.() || '';
  const marketId = marketData?.marketplace_id || marketData?._id;

  // Try exact name match first
  let found = markets.find(m => m?.name === key);
  if (found?.logo_uri) return found;

  // Try case-insensitive name match
  found = markets.find(m => m?.name?.toLowerCase?.() === keyLower);
  if (found?.logo_uri) return found;

  // Try matching by marketplace_id
  if (marketId) {
    found = markets.find(
      m =>
        m?._id === marketId ||
        (Array.isArray(m?.marketplace_id) &&
          m.marketplace_id.includes(marketId)),
    );
    if (found?.logo_uri) return found;
  }

  return undefined;
}

/**
 * Gets the icon URL for a marketplace.
 * Priority:
 * 1. Static icon from metadata (exact key match)
 * 2. Static icon from metadata (by type)
 * 3. logo_uri from marketData (API data for this specific item)
 * 4. logo from marketData (alternative API field)
 * 5. logo_uri from orgMarketplaces (organization's marketplace API data)
 * 6. Empty string (fallback)
 */
export function getMarketplaceIcon(
  key: string,
  marketData: MarketplaceIconData | null | undefined,
  metadata: StaticMetadata | unknown,
  orgMarketplaces?: OrgMarketplace[] | unknown[],
): string {
  if (!key && !marketData) {
    return '';
  }

  const keyLower = key?.toLowerCase?.() || '';
  const marketType = marketData?.type?.toLowerCase?.() || keyLower;
  const staticMeta = metadata as StaticMetadata | null | undefined;
  const metadataMarketplaces = staticMeta?.marketplaces;

  // 1. Try static metadata icon with exact key match
  if (metadataMarketplaces?.[keyLower]?.icon) {
    return metadataMarketplaces[keyLower].icon;
  }

  // 2. Try static metadata icon by marketplace type
  if (marketType && metadataMarketplaces?.[marketType]?.icon) {
    return metadataMarketplaces[marketType].icon;
  }

  // 3. Try logo_uri from marketData (API data for this specific item)
  if (marketData?.logo_uri) {
    return marketData.logo_uri;
  }

  // 4. Try logo from marketData (alternative field)
  if (marketData?.logo) {
    return marketData.logo;
  }

  // 5. Try logo_uri from orgMarketplaces (organization-level marketplace data)
  if (orgMarketplaces && Array.isArray(orgMarketplaces)) {
    const orgMarket = findOrgMarketplace(key, marketData, orgMarketplaces);
    if (orgMarket?.logo_uri) {
      return orgMarket.logo_uri;
    }
  }

  return '';
}

/**
 * Gets the display name for a marketplace type.
 */
export function getMarketplaceDisplayName(
  type: string,
  metadata: StaticMetadata | unknown,
): string {
  if (!type) {
    return '';
  }

  const staticMeta = metadata as StaticMetadata | null | undefined;
  const metadataMarketplaces = staticMeta?.marketplaces;
  const typeKey = type.toLowerCase();

  return metadataMarketplaces?.[typeKey]?.text || type;
}

/**
 * Returns the appropriate image source for marketplace icon.
 * For inactive/not-integrated local images, returns the -selected variant.
 * For CDN URLs, returns the original URL unchanged.
 */
export function getMarketplaceImageSrc(
  image: string,
  isInactiveOrNotIntegrated: boolean,
): string {
  if (!image) {
    return '';
  }

  if (isInactiveOrNotIntegrated && !image.startsWith('http')) {
    return image.replace(/\.([^.]+)$/, '-selected.$1');
  }

  return image;
}
